/**
 * Item content and answers, one schema per item type.
 *
 * Hand-written on purpose. On the wire `content`, `correctAnswer` and
 * `explanation` are opaque JSON (`ItemPayload` in the generated contract): their
 * shape is the item bank's content model, not the API's, so the OpenAPI document
 * cannot describe them. They are validated at cache time and again at render
 * time (§7.4).
 *
 * An unknown `type` is skipped with a warning, never a crash — which is why
 * `type` is a plain string on the wire and narrowed only here.
 */
import { z } from 'zod'

export const ITEM_TYPES = [
  'single-choice',
  'multi-choice',
  'numeric',
  'ordering',
  'passage-group',
] as const

export type ItemType = (typeof ITEM_TYPES)[number]

export const itemTypeSchema = z.enum(ITEM_TYPES)

export function isKnownItemType(type: string): type is ItemType {
  return (ITEM_TYPES as readonly string[]).includes(type)
}

// --- content ----------------------------------------------------------------

const optionSchema = z.object({
  id: z.string(),
  text: z.string(),
})

export const singleChoiceContentSchema = z.object({
  prompt: z.string(),
  hint: z.string().optional(),
  options: z.array(optionSchema).min(2),
})

export const multiChoiceContentSchema = singleChoiceContentSchema

export const numericContentSchema = z.object({
  prompt: z.string(),
  hint: z.string().optional(),
  answerFormat: z.enum(['integer', 'decimal', 'fraction']).default('decimal'),
  unit: z.string().optional(),
})

export const orderingContentSchema = z.object({
  prompt: z.string(),
  hint: z.string().optional(),
  tokens: z.array(optionSchema).min(2),
})

export const passageGroupContentSchema = z.object({
  prompt: z.string(),
  passage: z.string(),
  subItemIds: z.array(z.string()).default([]),
})

export const itemContentSchemas = {
  'single-choice': singleChoiceContentSchema,
  'multi-choice': multiChoiceContentSchema,
  numeric: numericContentSchema,
  ordering: orderingContentSchema,
  'passage-group': passageGroupContentSchema,
} as const satisfies Record<ItemType, z.ZodTypeAny>

export type SingleChoiceContent = z.infer<typeof singleChoiceContentSchema>
export type MultiChoiceContent = z.infer<typeof multiChoiceContentSchema>
export type NumericContent = z.infer<typeof numericContentSchema>
export type OrderingContent = z.infer<typeof orderingContentSchema>

// --- answers ----------------------------------------------------------------

export const singleChoiceAnswerSchema = z.object({ optionId: z.string() })
export const multiChoiceAnswerSchema = z.object({ optionIds: z.array(z.string()) })
export const numericAnswerSchema = z.object({ value: z.number() })
export const orderingAnswerSchema = z.object({ order: z.array(z.string()) })

export const itemAnswerSchemas = {
  'single-choice': singleChoiceAnswerSchema,
  'multi-choice': multiChoiceAnswerSchema,
  numeric: numericAnswerSchema,
  ordering: orderingAnswerSchema,
  // A passage group is answered through its sub-items, never directly.
  'passage-group': z.never(),
} as const satisfies Record<ItemType, z.ZodTypeAny>

// --- explanation ------------------------------------------------------------

export const explanationSchema = z.object({
  text: z.string(),
  steps: z.array(z.string()).optional(),
})

export type Explanation = z.infer<typeof explanationSchema>
